import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { MessageService } from '@corelabs/angular/messages';
import { TranslateService } from '@ngx-translate/core';
import { catchError, concatMap, filter, map, of, switchMap } from 'rxjs';
import { WarehouseError } from '@app/store/shared/models';
import { placedBoxesActions } from '../actions/placed-boxes.actions';
import { selectLockedCode } from '../selectors/placed-boxes.selector';
import { HttpService } from '../services/http.service';
import { finalizeLoader } from '@app/shared/components';

@Injectable()
export class PlacedBoxesEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);

    loadBoxes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(placedBoxesActions.loadBoxes),
            switchMap(({ stationCode, page }) => {
                return this.httpService.loadPlacedBoxes(stationCode, page).pipe(
                    finalizeLoader('placed-boxes-load'),
                    map(({ boxes, hasMore }) => placedBoxesActions.loadBoxesSuccess({ payload: boxes, page, hasMore })),
                    catchError((error: HttpErrorResponse) => of(placedBoxesActions.loadBoxesError({ code: error.status }))),
                );
            }),
        );
    });

    loadBox$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(placedBoxesActions.loadBox),
            switchMap(({ code }) => {
                return this.httpService.loadBox(code).pipe(
                    map((payload) => placedBoxesActions.loadBoxSuccess({ payload })),
                    catchError((error: HttpErrorResponse) => of(placedBoxesActions.loadBoxError({ code: error.status }))),
                );
            }),
        );
    });

    lockBox$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(placedBoxesActions.lockBox),
            switchMap(({ code }) => {
                return this.httpService.lockBox(code).pipe(
                    finalizeLoader('placed-boxes-lock'),
                    map((payload) => placedBoxesActions.lockBoxSuccess({ payload })),
                    catchError((error: WarehouseError) => {
                        this.messageService.add(this.translateService.instant(error.key), 'danger');
                        return of(placedBoxesActions.lockBoxError({ code: error.code, key: error.key }));
                    }),
                );
            }),
        );
    });

    unlockBox$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(placedBoxesActions.unlockBox),
            concatLatestFrom(() => this.store.select(selectLockedCode)),
            filter(([, lockedCode]) => lockedCode !== null),
            concatMap(([, lockedCode]) => {
                const cartonCode = lockedCode!;
                return this.httpService.unlockBox(cartonCode).pipe(
                    map(() => placedBoxesActions.unlockBoxSuccess({ cartonCode })),
                    catchError((error: HttpErrorResponse) => of(placedBoxesActions.unlockBoxError({ code: error.status, cartonCode }))),
                );
            }),
        );
    });

    reduceItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(placedBoxesActions.reduceItem),
            concatMap(({ code, ean }) => {
                return this.httpService.reduceItem(code, ean).pipe(
                    map((payload) => placedBoxesActions.reduceItemSuccess({ payload })),
                    catchError((error: WarehouseError) => {
                        this.messageService.add(this.translateService.instant(error.key), 'danger');
                        return of(placedBoxesActions.reduceItemError({ code: error.code, key: error.key }));
                    }),
                );
            }),
        );
    });

    increaseItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(placedBoxesActions.increaseItem),
            concatMap(({ code, ean }) => {
                return this.httpService.increaseItem(code, ean).pipe(
                    map((payload) => placedBoxesActions.increaseItemSuccess({ payload })),
                    catchError((error: WarehouseError) => {
                        this.messageService.add(this.translateService.instant(error.key), 'danger');
                        return of(placedBoxesActions.increaseItemError({ code: error.code, key: error.key }));
                    }),
                );
            }),
        );
    });

    closeBox$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(placedBoxesActions.closeBox),
            concatMap(({ code }) => {
                return this.httpService.closeBox(code).pipe(
                    finalizeLoader('placed-boxes-close'),
                    map((payload) => placedBoxesActions.closeBoxSuccess({ code, payload })),
                    catchError((error: WarehouseError) => {
                        if (error.reason === 'duplicate_request') {
                            return of(placedBoxesActions.closeBoxSuccess({ code, payload: null }));
                        }
                        this.messageService.add(this.translateService.instant(error.key), 'danger');
                        return of(placedBoxesActions.closeBoxError({ code: error.code, key: error.key }));
                    }),
                );
            }),
        );
    });
}
